import { useQuery } from '@tanstack/react-query';
import { Loader2, X, History } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Who changed this pickup location, when, and what it was before.
 *
 * Internal staff only — the API refuses a merchant, and the column that opens this is not rendered
 * for one. A pickup dispute is raised weeks after the fact ("the courier went to the old address"),
 * which is exactly when the BEFORE value matters and nobody can remember it.
 */
type Change = { field: string; label?: string; before?: any; after?: any };
type Row = {
  id: string; action: string; actorName?: string | null; actorEmpCode?: string | null;
  actorType: string; createdAt: string; changes: Change[]; ip?: string | null;
};

export function WarehouseHistoryDialog({ warehouse, onClose }: {
  warehouse: { id: number; name: string } | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['warehouse-edit-logs', warehouse?.id],
    queryFn: async () => (await api.get(`/warehouses/${warehouse!.id}/edit-logs`)).data as Row[],
    enabled: !!warehouse,
  });
  if (!warehouse) return null;

  const show = (v: any) => {
    if (v === null || v === undefined || v === '') return <span className="text-slate-300">empty</span>;
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><History size={16} /> Edit history</h3>
            <p className="text-xs text-slate-500">{warehouse.name} · #{warehouse.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {isLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : !(data ?? []).length ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No changes recorded yet. History starts from the first edit after this was switched on.
          </div>
        ) : (
          <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
            {(data ?? []).map((r) => (
              <div key={r.id} className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 text-xs">
                  <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">{r.action}</span>
                  <span className="text-slate-800 font-medium">{r.actorName ?? 'Unknown'}</span>
                  {r.actorEmpCode && <span className="text-slate-400">({r.actorEmpCode})</span>}
                  {/* A merchant editing their own location reads very differently from one of ours doing it. */}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${r.actorType === 'merchant' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    {r.actorType === 'merchant' ? 'Merchant' : 'Internal'}
                  </span>
                  <span className="text-slate-500 ml-auto">{new Date(r.createdAt).toLocaleString('en-IN')}</span>
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {(r.changes ?? []).map((c, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-slate-500 w-44">{c.label ?? c.field}</td>
                        <td className="px-3 py-1.5 text-rose-700 line-through">{show(c.before)}</td>
                        <td className="px-3 py-1.5 text-emerald-700">{show(c.after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
